import { useEffect, useRef, type KeyboardEvent, type PointerEvent } from 'react'
import { Button } from '../../ui'

/** A press held this long opens the message's menu — `Row`'s own long-press
 *  length, so the whole app agrees on what a long press is. */
const HOLD_MS = 450
/** A finger that travels further than this is scrolling, not holding. */
const SLOP_PX = 10

/**
 * One text part. Doug's in a filled bubble on the right; the butler's
 * full-width with no bubble at all — it is the app talking, not a contact.
 *
 * Paragraphs on a blank line and nothing else: no markdown renderer. The
 * prompt forbids markdown, so a stray `**` drawn literally is the correct
 * bug report rather than something to hide.
 *
 * WP 9.12: `caret` draws the streaming caret after the last word, and
 * `onHold` makes the text a long-press target for its message's menu
 * (`MessageMenu` below — Retry, Edit, Copy, and the time). The same menu
 * opens on a right-click (a desktop, and Android's own long-press), and on
 * the context-menu key or Shift+F10 once the text has focus, so it is never
 * touch-only. Holding text on iOS would otherwise start a selection and its
 * callout, so a holdable text turns both off — which is why Copy is in the
 * menu: the menu takes over the gesture that used to copy.
 */
export function Bubble({ role, text, caret = false, onHold }: {
  role: 'user' | 'assistant'
  text: string
  caret?: boolean
  onHold?: () => void
}) {
  const paras = text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean)
  const timer = useRef<number | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  useEffect(() => () => { if (timer.current != null) window.clearTimeout(timer.current) }, [])

  if (paras.length === 0) return null

  const cancel = () => {
    if (timer.current != null) window.clearTimeout(timer.current)
    timer.current = null
    start.current = null
  }
  const hold = onHold && {
    tabIndex: 0,
    'data-holdable': '',
    onPointerDown: (e: PointerEvent) => {
      if (e.button !== 0) return
      fired.current = false
      start.current = { x: e.clientX, y: e.clientY }
      timer.current = window.setTimeout(() => { fired.current = true; timer.current = null; onHold() }, HOLD_MS)
    },
    onPointerMove: (e: PointerEvent) => {
      const s = start.current
      if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > SLOP_PX) cancel()
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onContextMenu: (e: { preventDefault: () => void }) => {
      e.preventDefault()
      cancel()
      // A touch long-press can fire both the timer and `contextmenu`; one
      // menu, not two toggles.
      if (!fired.current) onHold()
      fired.current = false
    },
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) { e.preventDefault(); onHold() }
    },
  }

  return (
    <div className={role === 'user' ? 'chat-bubble' : 'chat-say'} {...hold}>
      {paras.map((p, i) => (
        <p key={i}>
          {p}
          {caret && i === paras.length - 1 && <span className="chat-caret" aria-hidden="true" />}
        </p>
      ))}
    </div>
  )
}

export interface MenuAction { label: string; onClick: () => void }

/**
 * What a long-press on a message opens: its time, then what can be done
 * with it — built from `Button`s, not a popover (no primitive is added for
 * this). It sits under the message it belongs to and goes on a tap anywhere
 * else, on Escape, or once an action has run.
 */
export function MessageMenu({ stamp, actions, align, onClose }: {
  stamp: string | null
  actions: MenuAction[]
  align: 'start' | 'end'
  onClose: () => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    // Focus the first action, so a menu opened from the keyboard is usable
    // from the keyboard; a touch never sees the focus ring.
    box.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true })
    const away = (e: Event) => {
      if (box.current && e.target instanceof Node && box.current.contains(e.target)) return
      closeRef.current()
    }
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    // Registered a frame late: the release of the press that opened the menu
    // must not be the tap that closes it.
    const id = window.setTimeout(() => {
      document.addEventListener('pointerdown', away)
      document.addEventListener('keydown', esc)
    }, 0)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [])

  return (
    <div ref={box} className={`chat-menu chat-menu--${align}`} role="group" aria-label="Message">
      {stamp && <span className="chat-menu__stamp">{stamp}</span>}
      {actions.map(a => (
        <Button key={a.label} variant="quiet" size="sm" onClick={() => { a.onClick(); closeRef.current() }}>
          {a.label}
        </Button>
      ))}
    </div>
  )
}
